import {
  LEGACY_PARITY_EVENT_NAMES,
  sanitizeProductProperties,
  scrubCredentialProperties,
  type CaptureOptions,
} from '@galapagos/product-analytics';
import posthog, { type CaptureResult, type PostHog } from 'posthog-js';
import type { AnalyticsProvider, AnalyticsTransport } from '../types';
import type { PostHogConfig } from '../analytics-resolve';
import { guardProductAnalytics } from './product-analytics';
import { postHogProperties, postHogTimestamp, postHogUuid } from './posthog-capture';
import { isPresignedUrl } from './presigned-url';
import { withoutSearchTerms } from './search-term-url';
import {
  createPostHogBeforeSend,
  INERT_REPLAY_CONTROLS,
  type ReplayControls,
} from './posthog-before-send';

export {
  createSuppressedPathBeforeSend,
  createPostHogBeforeSend,
  holdSessionReplay,
  resetNavigationListeners,
  resetReplayHolds,
} from './posthog-before-send';

/**
 * Web AnalyticsProvider backed by `posthog-js`.
 *
 * The ONLY `posthog-js` import in the package. Autocapture is enabled by default
 * for chart exploration. The BAA-covered product data plane accepts URLs, text,
 * identifiers, and PHI/PII; the sanitizer removes credentials and auth secrets.
 * Session replay remains an independent control with client-owned masking and
 * production sampling floors that remote configuration cannot weaken.
 */

const DEFAULT_HOST = 'https://us.i.posthog.com';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** PostHog's own exception event; its stack payload is vendor-shaped, not a chart property. */
const EXCEPTION_EVENT = '$exception';

function sanitizePostHogProperties(
  properties: Record<string, unknown>,
  eventName?: string,
): Record<string, unknown> {
  // posthog-js stores the project API key on every event as `token` before it
  // calls this hook, and PostHog rejects an event that arrives without it. The
  // shared secret rules match that bare key, so it is lifted out here and put
  // back on every return shape; a bag that never carried it is returned as is.
  const { token, ...rest } = properties;
  const keepToken = (bag: Record<string, unknown>): Record<string, unknown> =>
    'token' in properties ? { ...bag, token } : bag;
  if (eventName === EXCEPTION_EVENT) {
    // ADR-081 Rule 8 with the stack intact: secret-like keys are removed and
    // secret-shaped values redacted, without the structural-limit sanitizer
    // below, which would replace a deep `$exception_list[].stacktrace.frames`
    // array wholesale. `sanitize_properties` cannot reject an event, so a
    // payload that scrubs to nothing (every top-level key secret-like, which a
    // real SDK `$exception` never is because it always carries
    // `$exception_list`) is sent as a marker carrying no data rather than the
    // original.
    const scrubbed = scrubCredentialProperties(rest);
    if (!isRecord(scrubbed)) return keepToken({ $analytics_sanitization_failed: true });
    return keepToken(
      Object.keys(scrubbed).length === 0 ? { $analytics_secret_only_payload: true } : scrubbed,
    );
  }
  try {
    return keepToken(sanitizeProductProperties(rest).properties);
  } catch {
    return keepToken({ $analytics_sanitization_failed: true });
  }
}

const INERT: AnalyticsTransport = {
  track: () => {},
  setUser: () => {},
  clearUser: () => {},
};

export function createPostHogWebProvider(config: PostHogConfig): AnalyticsProvider {
  const { key, host, captureSyntheticTraffic } = config;
  if (!key) return guardProductAnalytics(INERT);

  const sampleRate = config.sessionReplaySampleRate;
  if (
    sampleRate !== undefined &&
    (!Number.isFinite(sampleRate) || sampleRate < 0 || sampleRate > 1)
  ) {
    throw new Error('PostHog session replay sample rate must be between 0 and 1');
  }
  const production = ['prod', 'production'].includes(config.environment?.toLowerCase() ?? '');
  if (production && config.sessionReplay === true && sampleRate === undefined) {
    throw new Error('Production PostHog session replay requires an explicit sample rate');
  }

  const autocapture = config.autocapture !== false;
  // Replay switches reach the SDK only when replay was explicitly enabled:
  // posthog-js `startSessionRecording()` also clears `disable_session_recording`,
  // so a pause that resumed unconditionally would turn replay ON where it is
  // off by default (production).
  const replayControls: ReplayControls =
    config.sessionReplay === true
      ? {
          stopSessionRecording: () => posthog.stopSessionRecording(),
          startSessionRecording: () => posthog.startSessionRecording(),
        }
      : INERT_REPLAY_CONTROLS;
  posthog.init(key, {
    api_host: host ?? DEFAULT_HOST,
    autocapture,
    disable_session_recording: config.sessionReplay !== true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: 'img,video,canvas,iframe,[data-ph-no-capture],.ph-no-capture',
      recordHeaders: false,
      recordBody: false,
      // A captured network request keeps its full URL, query string included;
      // a presigned link's query is the credential, so the request is dropped,
      // and a free-text search term is removed from the URL it rides on. An
      // embedded frame's load is dropped too: replay already blocks the frame,
      // and its URL can carry personal details a third-party embed is
      // pre-filled with.
      maskCapturedNetworkRequestFn: (request) => {
        if (isPresignedUrl(request.name) || request.initiatorType === 'iframe') return null;
        const name = withoutSearchTerms(request.name);
        if (name === null) return null;
        return name === request.name ? request : { ...request, name };
      },
      ...(sampleRate === undefined ? {} : { sampleRate }),
    },
    capture_pageview: autocapture ? 'history_change' : false,
    capture_pageleave: autocapture,
    capture_heatmaps: autocapture,
    capture_dead_clicks: autocapture,
    ...(config.maskAutocaptureContent === true
      ? { mask_all_text: true, mask_all_element_attributes: true }
      : {}),
    advanced_disable_feature_flags: true,
    opt_out_useragent_filter: captureSyntheticTraffic === true,
    ...(config.captureExceptions === true
      ? {
          capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: true,
          },
        }
      : {}),
    sanitize_properties: sanitizePostHogProperties,
    // Always installed: dropping autocapture from a `ph-no-capture` subtree
    // needs no configuration. Every other stage is inert unless configured.
    before_send: createPostHogBeforeSend(
      {
        suppressedPathPatterns: config.suppressedPathPatterns,
        heatmapExcludedPathPatterns: config.heatmapExcludedPathPatterns,
        heatmapFixedPointExcludedPathPatterns: config.heatmapFixedPointExcludedPathPatterns,
        entrySuppressedVisits: config.entrySuppressedVisits,
        entryAttributionExcludedPathPatterns: config.entryAttributionExcludedPathPatterns,
        autocaptureExcludedPathPatterns: config.autocaptureExcludedPathPatterns,
        pageGeometryExcludedPathPatterns: config.pageGeometryExcludedPathPatterns,
        previousPageviewExcluded: config.previousPageviewExcluded,
        replayPausedPathPatterns: config.replayPausedPathPatterns,
        replayPausedWhileHeld: config.replayPausedWhileHeld,
        maskAutocaptureContent: config.maskAutocaptureContent,
        urlPageTemplates: config.urlPageTemplates,
      },
      replayControls,
    ),
  });

  const transport: AnalyticsTransport = {
    track: (event, properties, options) =>
      posthog.capture(event, postHogProperties(properties, options, event), {
        timestamp: postHogTimestamp(options),
        uuid: postHogUuid(options?.insertId),
      }),
    setUser: (userId, properties) => posthog.identify(userId, properties),
    clearUser: () => posthog.reset(),
  };

  return guardProductAnalytics(transport);
}

/**
 * Configuration for the legacy PostHog mirror: the project the growth landing
 * pages report to. Both values come from build-time public config; either one
 * missing leaves the mirror inert.
 */
export interface PostHogLegacyMirrorConfig {
  key?: string;
  host?: string;
}

/** The posthog-js instance name the mirror runs under, apart from the default instance. */
export const LEGACY_MIRROR_INSTANCE_NAME = 'pelago_legacy';

const LEGACY_MIRROR_EVENTS: ReadonlySet<string> = new Set(LEGACY_PARITY_EVENT_NAMES);

/**
 * The subject the mirror is identifying right now. It is set only for the
 * duration of the mirror's own identify call, which runs the egress hook
 * synchronously, so no other `$identify` can be admitted.
 */
let pendingLegacyIdentify: string | undefined;

/**
 * The mirror's last hook before the network: anything that is not one of the
 * legacy-parity events is dropped, including events the SDK generates on its
 * own. The init options already switch every such path off; this holds even if
 * remote config or a later SDK version switches one back on. The one exception
 * is the `$identify` the mirror itself requested, sent without person
 * properties.
 */
export function legacyMirrorBeforeSend(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null;
  if (LEGACY_MIRROR_EVENTS.has(event.event)) return event;
  if (event.event !== '$identify') return null;
  if (pendingLegacyIdentify === undefined) return null;
  const { properties } = event;
  if (properties.distinct_id !== pendingLegacyIdentify) return null;
  const { $set: _set, $set_once: _setOnce, ...strippedProperties } = properties;
  const { $set: _topSet, $set_once: _topSetOnce, ...rest } = event;
  return { ...rest, properties: strippedProperties };
}

/** What the legacy instance knows about the visitor's current identity. */
export interface LegacyMirrorIdentityState {
  distinctId: string | undefined;
  deviceId: string | undefined;
  userState: string | undefined;
}

/**
 * Whether the mirror should identify the legacy person as `subject`. A person
 * the landing pages or legacy signup already identified keeps their id, so
 * their history is not split. Cookies written before the SDK recorded a user
 * state count as anonymous only while the distinct id is still the device id.
 */
export function legacyMirrorShouldIdentify(
  state: LegacyMirrorIdentityState,
  subject: string,
): boolean {
  if (state.distinctId === subject) return false;
  if (state.userState === 'identified') return false;
  if (state.userState === 'anonymous') return true;
  return state.distinctId !== undefined && state.distinctId === state.deviceId;
}

function stringProperty(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function legacyMirrorIdentityState(instance: PostHog): LegacyMirrorIdentityState {
  return {
    distinctId: stringProperty(instance.get_distinct_id()),
    deviceId: stringProperty(instance.get_property('$device_id')),
    userState: stringProperty(instance.get_property('$user_state')),
  };
}

let legacyMirrorInstance: { key: string; host: string; instance: PostHog } | undefined;

function legacyMirrorClient(key: string, host: string): PostHog {
  if (legacyMirrorInstance?.key === key && legacyMirrorInstance.host === host) {
    return legacyMirrorInstance.instance;
  }
  const instance = posthog.init(
    key,
    {
      api_host: host,
      // Configured like the landing pages' own client, so this instance reads
      // the visitor cookie they set on the parent domain and sends under the
      // same id. Every path other than an explicit capture() is off.
      // The parent-domain cookie the landing pages write is the only store
      // read. With local storage in the mix, an id an earlier app left in this
      // origin's storage wins over the landing page's cookie and the visitor
      // splits into two people.
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      person_profiles: 'always',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      rageclick: false,
      capture_performance: false,
      capture_exceptions: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_product_tours: true,
      disable_conversations: true,
      disable_web_experiments: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      advanced_disable_feature_flags: true,
      sanitize_properties: sanitizePostHogProperties,
      before_send: legacyMirrorBeforeSend,
    },
    LEGACY_MIRROR_INSTANCE_NAME,
  );
  legacyMirrorInstance = { key, host, instance };
  return instance;
}

/**
 * A second, narrow PostHog sink for the legacy project the growth landing pages
 * report to, so a landing-page visitor and their signup stay one person there
 * (ADR-167). It forwards only the legacy-parity events. On sign-in it
 * identifies an anonymous legacy person with the member's analytics subject,
 * merging the landing-page visitor into the member; it never aliases,
 * registers, sets person properties or resets. It is inert without a key and
 * host, and a failing init leaves it inert rather than stopping the providers
 * built beside it.
 */
export function createPostHogLegacyMirrorProvider(
  config: PostHogLegacyMirrorConfig,
): AnalyticsProvider {
  const key = config.key?.trim();
  const host = config.host?.trim();
  if (!key || !host) return guardProductAnalytics(INERT);

  let instance: PostHog;
  try {
    instance = legacyMirrorClient(key, host);
  } catch {
    return guardProductAnalytics(INERT);
  }

  const transport: AnalyticsTransport = {
    track: (event, properties, options) => {
      if (!LEGACY_MIRROR_EVENTS.has(event)) return;
      // The guard marks pre-sign-in events as personless for the primary
      // project. Here the person belongs to the legacy project, which profiles
      // every visitor, so that marker is left off to keep the event on the
      // landing-page visitor's person.
      const mirrorOptions: CaptureOptions = { ...options, processPersonProfile: undefined };
      instance.capture(event, postHogProperties(properties, mirrorOptions, event), {
        timestamp: postHogTimestamp(mirrorOptions),
        uuid: postHogUuid(mirrorOptions.insertId),
      });
    },
    // Person properties are never sent to the legacy project.
    setUser: (subject) => {
      try {
        if (!legacyMirrorShouldIdentify(legacyMirrorIdentityState(instance), subject)) return;
        // posthog-js runs before_send synchronously inside identify, so the
        // authorisation below never outlives this call.
        pendingLegacyIdentify = subject;
        try {
          instance.identify(subject);
        } finally {
          pendingLegacyIdentify = undefined;
        }
      } catch {
        // A failing mirror never breaks the caller.
      }
    },
    // The landing pages own the legacy cookie, so it is never reset here.
    clearUser: () => {},
  };

  // Filtered before the guard as well, so the many canonical events every page
  // sends are not validated a second time only to be dropped.
  const guarded = guardProductAnalytics(transport);
  return {
    ...guarded,
    track: (event, properties, options) => {
      if (LEGACY_MIRROR_EVENTS.has(event)) guarded.track(event, properties, options);
    },
  };
}

export function resetLegacyMirrorForTests(): void {
  legacyMirrorInstance = undefined;
}

export function legacyMirrorInstanceForTests(): PostHog | undefined {
  return legacyMirrorInstance?.instance;
}
